"use client";

import { useTournaments } from "@repo/api-client";
import Image from "next/image";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useAuth } from "./hooks/useAuth";

export default function HomePage() {
  const { t } = useTranslation('tournaments');
  const { t: tCommon } = useTranslation('common');
  const { t: tAuth } = useTranslation('auth');
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: tournaments, isLoading: tournamentsLoading } = useTournaments();

  if (authLoading || tournamentsLoading) {
    return <div>{tCommon('loading')}</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-4 dark:text-white">{t('welcome_to_euro_sportring')}</h1>
        <p className="mb-4 dark:text-white">{t('please_log_in_to_view_tournaments')}</p>
        <Link
          href="/login"
          className="inline-block px-4 py-2 bg-secondary-700 text-white rounded hover:bg-secondary-800 dark:bg-secondary-600 dark:hover:bg-secondary-700"
        >
          {tAuth('login')}
        </Link>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6 dark:text-white">{t('your_tournaments')}</h1>
      {tournaments && tournaments.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tournaments.map((tournament) => (
            <div
              key={tournament.clubTournamentID}
              className="border border-gray-200 dark:border-primary-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-primary-600"
            >
              <h2 className="text-xl font-semibold mb-2 dark:text-white">
                {tournament.clubTournamentData.projectName}
              </h2>
              <div className="text-sm text-gray-600 dark:text-white space-y-1">
                <p>
                  {t('location')}: {tournament.clubTournamentData.projectDestination},{" "}
                  {tournament.clubTournamentData.countryName}
                </p>
                <p>{t('period')}: {tournament.periodDescription}</p>
                <p>{t('categories')}: {tournament.joinedCategoryCodes}</p>
                <p className="mt-2">
                  <span
                    className={`inline-block px-2 py-1 rounded text-sm ${
                      tournament.isAvailable
                        ? "bg-green-100 dark:bg-green-700 text-green-800 dark:text-white"
                        : "bg-red-100 dark:bg-red-700 text-red-800 dark:text-white"
                    }`}
                  >
                    {tournament.isAvailable ? tCommon('available') : tCommon('not_available')}
                  </span>
                </p>
              </div>
              {tournament.logoUrl && (
                <Image
                  src={tournament.logoUrl}
                  alt={`${tournament.clubTournamentData.projectName} logo`}
                  width={400}
                  height={128}
                  className="mt-4 w-full h-32 object-contain"
                />
              )}
              <pre className="mt-4 text-xs bg-gray-50 dark:bg-primary-700 dark:text-white p-2 rounded overflow-auto">
                {JSON.stringify(tournament, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-600 dark:text-white">{t('no_tournaments_found')}</div>
      )}
    </div>
  );
}